// Settings -> Configuration (documentation/configuration.md). Platform administration only; the
// server enforces that. Every call goes to the RadioOS backend: the browser never talks to
// Hetzner, Simply or Microsoft, and no response contains a secret value (only metadata such as
// "configured" and its source).

export type ConfigSection = 'entra' | 'hetzner' | 'simply' | 'livekit' | 'deployment' | 'onence';

export interface SecretStatus {
  configured: boolean;
  source: 'external-file' | 'environment' | 'none';
  updated_at?: string;
  error?: string;
}

export interface LastCheck {
  at?: string;
  success: boolean;
  detail: string;
  actor: string;
}

export interface Issue {
  field: string;
  code: string;
  severity: 'error' | 'warning';
  message: string;
}

export interface ReportMeta {
  state: 'not_configured' | 'not_reported' | 'invalid' | 'reported';
  generated_at?: string;
  error?: string;
}

export interface Meta {
  updated_at?: string;
  updated_by?: string;
}

export interface CredentialView {
  state: 'not_reported' | 'configured' | 'missing' | 'invalid';
  source?: string;
  last_verified_at?: string;
  held_by: string;
}

export interface ConnectionReport {
  state: 'connected' | 'failed';
  checked_at?: string;
  error_code?: string;
}

export interface OperationReport {
  operation: string;
  result: string;
  at: string;
  summary?: string;
}

export interface EntraView {
  status: string;
  enabled: boolean;
  tenant_id: string;
  client_id: string;
  authority_host: string;
  redirect_url: string;
  post_logout_url: string;
  client_secret: SecretStatus;
  credential_key_id: string;
  credential_expires: string;
  credential_expires_in_days?: number;
  password_login_enabled: boolean;
  /** The session lifetime policy (Settings -> Configuration -> Sessions) and the cookie mode. */
  session: { admin_session_minutes?: number; dispatcher_session_hours?: number; cookie_secure: boolean };
  linked_identities: number;
  linked_platform_admins: number;
  managed_by: string;
  last_check: LastCheck | null;
}

export interface LiveKitView {
  status: string;
  public_url: string;
  media_host: string;
  internal_url: string;
  api_key: string;
  api_secret: SecretStatus;
  public_profile: { active: boolean; public_url_valid: boolean };
  managed_by: string;
  last_check: LastCheck | null;
}

export interface HetznerSettings {
  project: string;
  location: string;
  server_type: string;
  image: string;
  server_name: string;
  ssh_key_name: string;
  network_range: string;
  subnet_range: string;
  private_ipv4: string;
}

export interface InstanceView {
  state: string;
  id?: string;
  name?: string;
  region?: string;
  flavor?: string;
  public_ipv4?: string;
  basis: string;
}

export interface HetznerServer {
  id: string;
  name: string;
  status: string;
  location: string;
  server_type: string;
  image?: string;
  public_ipv4?: string;
  private_ipv4?: string;
  firewall?: 'applied' | 'pending' | 'none';
  ssh_keys?: string[];
}

export interface HetznerNetwork {
  id: string;
  name?: string;
  ip_range: string;
  subnets?: string[];
}

export interface ObjectStorageBucket {
  bucket: string;
  location: string;
  object_lock: 'enabled' | 'disabled';
  versioning: 'enabled' | 'suspended' | 'disabled';
  retention_mode?: 'GOVERNANCE' | 'COMPLIANCE';
  retention_days?: number;
}

export interface OffsiteBackup {
  last_at?: string;
  snapshot?: string;
  result?: string;
}

export interface FieldComparison {
  field: keyof HetznerSettings;
  desired: string;
  actual?: string;
  state: 'in_sync' | 'drift' | 'not_reported';
}

export interface HetznerView {
  provider: 'hetzner';
  environment: string;
  status: string;
  desired: HetznerSettings;
  meta: Meta;
  issues: Issue[];
  complete: boolean;
  report: ReportMeta;
  credential: CredentialView;
  connection?: ConnectionReport;
  server?: HetznerServer;
  server_basis: string;
  network?: HetznerNetwork;
  object_storage?: ObjectStorageBucket;
  dns_endpoint?: string;
  last_operation?: OperationReport;
  offsite_backup?: OffsiteBackup;
  comparison: FieldComparison[];
  drift: string[];
  instance: InstanceView;
}

export interface SimplySettings {
  zone: string;
  ttl: number;
}

export interface DNSObservation {
  name: string;
  state: 'resolves' | 'nxdomain' | 'error' | 'not_checked';
  addresses: string[];
  checked_at?: string;
}

export interface RecordView {
  name: string;
  type: string;
  desired_value?: string;
  desired_basis: string;
  provider_value?: string;
  provider_ttl?: number;
  public: DNSObservation;
  sync: 'in_sync' | 'missing' | 'drift' | 'pending' | 'unknown';
}

export interface SimplyView {
  provider: 'simply';
  environment: string;
  status: string;
  desired: SimplySettings;
  meta: Meta;
  issues: Issue[];
  complete: boolean;
  report: ReportMeta;
  credential: CredentialView;
  connection?: ConnectionReport;
  records: RecordView[];
  last_operation?: OperationReport;
}

export interface DeploymentSettings {
  public_host: string;
  media_host: string;
}

export interface TargetView {
  environment: string;
  desired: DeploymentSettings;
  meta: Meta;
  issues: Issue[];
  report: ReportMeta;
  vm: InstanceView;
  public_ipv4?: string;
  dns: DNSObservation[];
  tls: TLSView;
  deployment: { state: string; release?: string; commit?: string; deployed_at?: string; basis: string };
  backup: { state: string; last_at?: string; offsite?: OffsiteBackup };
  drift: string[];
  blockers: string[];
}

export interface TLSRenewal {
  scheduler: 'systemd-timer' | 'none';
  enabled: boolean;
  next_check_at?: string;
  last_check_at?: string;
  last_result?: 'renewed' | 'no-renewal-needed' | 'failed';
  last_success_at?: string;
  last_renewed_at?: string;
  last_dry_run_at?: string;
}

/** Certificate state from the deployment's sanitized report; health is computed by the server. */
export interface TLSView {
  state: string;
  not_after?: string;
  issuer?: string;
  basis: string;
  health: 'healthy' | 'warning' | 'critical' | 'unknown';
  reasons: string[];
  names: string[];
  missing_names: string[];
  days_remaining?: number;
  renewal?: TLSRenewal;
}

export interface InstanceInfo {
  environment: string;
  profile: 'public' | 'local-development';
  dev_mode: boolean;
  release: string;
  public_origin: string;
  media_url: string;
  entra_enabled: boolean;
  password_login_enabled: boolean;
  control_plane_reports: boolean;
  managed_environment: string;
}

export interface DeploymentView {
  instance: InstanceInfo;
  target: TargetView;
}

export interface Overview extends DeploymentView {
  environment: string;
  sections: Record<ConfigSection, string>;
}

export interface AuditEntry {
  id: string;
  created_at: string;
  actor: string;
  environment: string;
  provider: string;
  operation: string;
  success: boolean;
  changed_fields: { field: string; from: unknown; to: unknown }[];
  detail: string;
}

export class ConfigurationError extends Error {
  constructor(message: string, public code?: string, public issues: Issue[] = []) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/admin/configuration${path}`, {
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new ConfigurationError(body.error || `Request failed (${response.status})`, body.code, body.issues || []);
  return body as T;
}

/** Settings -> Configuration -> Sessions: the global human-session lifetime policy. */
export interface SessionPolicyView {
  /** Management Client (Admin) sessions: minutes from sign-in. */
  admin_session_minutes: number;
  /** Dispatcher sessions: hours from sign-in. */
  dispatcher_session_hours: number;
  revision: number;
  updated_at: string;
  updated_by?: string;
  bounds: {
    admin_session_minutes: { min: number; max: number; default: number };
    dispatcher_session_hours: { min: number; max: number; default: number };
  };
  live_sessions: { admin: number; dispatcher: number };
  /** PUT only: whether anything changed, and how many live sessions got a new expiry. */
  changed?: boolean;
  adjusted_sessions?: { admin: number; dispatcher: number };
}

export const getSessionPolicy = () => request<SessionPolicyView>('/sessions');
export const saveSessionPolicy = (adminMinutes: number, dispatcherHours: number, revision: number) =>
  request<SessionPolicyView>('/sessions', {
    method: 'PUT',
    body: JSON.stringify({ admin_session_minutes: adminMinutes, dispatcher_session_hours: dispatcherHours, revision }),
  });

/** A whole number within [min, max], or null (the server validates the same). */
export function boundedInteger(raw: string, min: number, max: number): number | null {
  if (!/^\d{1,4}$/.test(raw.trim())) return null;
  const n = Number(raw.trim());
  return n >= min && n <= max ? n : null;
}

export const getOverview = () => request<Overview>('');
export const getEntra = () => request<EntraView>('/entra');
export const getLiveKit = () => request<LiveKitView>('/livekit');
export const getHetzner = () => request<HetznerView>('/hetzner');
export const getSimply = () => request<SimplyView>('/simply');
export const getDeployment = () => request<DeploymentView>('/deployment');
export const getAudit = () => request<AuditEntry[]>('/audit');
export const testEntra = () => request<{ success: boolean; result: { discovery: string; credential: string; error_code?: string } }>('/entra/test', { method: 'POST' });
export const testLiveKit = () => request<{ success: boolean; detail: string }>('/livekit/test', { method: 'POST' });

type Owner = 'hetzner' | 'simply' | 'deployment';
export const saveSettings = (owner: Owner, settings: HetznerSettings | SimplySettings | DeploymentSettings) =>
  request<{ changed_fields: AuditEntry['changed_fields']; issues: Issue[] }>(`/${owner}`, { method: 'PUT', body: JSON.stringify(settings) });
export const validateSettings = (owner: Owner) =>
  request<{ valid: boolean; complete: boolean; issues: Issue[] }>(`/${owner}/validate`, { method: 'POST' });
